import { Octokit } from "@octokit/rest";
import { retry } from "@octokit/plugin-retry";
import { wipeSession } from "@/lib/auth/session";
import { clearPersistedCache } from "@/lib/persist/query-persister";
import { notify } from "@/lib/toast";

const OctokitWithRetry = Octokit.plugin(retry);

/**
 * Factory for an Octokit instance bound to a token.
 * Composes @octokit/plugin-retry for automatic 5xx/429 retry (3 attempts,
 * exponential backoff). 4xx errors are not retried (doNotRetry list).
 * Uses request hooks to intercept 401s → clear session and redirect to `/?auth=expired`.
 */
export function createOctokit(token: string): InstanceType<typeof OctokitWithRetry> {
  const octokit = new OctokitWithRetry({
    auth: token,
    userAgent: "github-ci-analyzer",
    retry: {
      doNotRetry: [400, 401, 403, 404, 422],
      retries: 3,
    },
  });

  octokit.hook.error("request", (error) => {
    const status = (error as { status?: number }).status;
    if (status === 401) {
      // Emit a toast while still on the current page; the redirect happens next.
      notify.error("Token invalid or expired", {
        dedupeKey: "auth-401",
        description: "Redirecting to login…",
      });
      wipeSession();
      // Fire-and-forget IDB wipe: location.replace reloads the page and
      // awaiting would block for no real benefit — the landing reload
      // re-mounts a clean PersistQueryClientProvider.
      void clearPersistedCache();
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.replace("/?auth=expired");
      }
    }
    throw error;
  });

  return octokit;
}
